/**
 * Geometric bounds of an SVG path string in the glyph's normalized coord
 * system (y=0 baseline, +y up). Walks every drawing command including
 * cubic and quadratic Bézier curve extrema — this is what the eye sees,
 * not just on-curve endpoints.
 */

/** Roots in (0,1) where d/dt of a cubic in `p0..p3` is zero. */
function cubExt(p0: number, p1: number, p2: number, p3: number): number[] {
    const A = -p0 + 3 * p1 - 3 * p2 + p3
    const B = 2 * p0 - 4 * p1 + 2 * p2
    const C = p1 - p0
    const ts: number[] = []
    const EPS = 1e-9
    if (Math.abs(A) < EPS) {
        if (Math.abs(B) > EPS) {
            const t = -C / B
            if (t > 0 && t < 1) ts.push(t)
        }
    } else {
        const disc = B * B - 4 * A * C
        if (disc >= 0) {
            const s = Math.sqrt(disc)
            const t1 = (-B + s) / (2 * A)
            const t2 = (-B - s) / (2 * A)
            if (t1 > 0 && t1 < 1) ts.push(t1)
            if (t2 > 0 && t2 < 1) ts.push(t2)
        }
    }
    return ts
}

function cubAt(p0: number, p1: number, p2: number, p3: number, t: number): number {
    const u = 1 - t
    return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3
}

function quadExt(p0: number, p1: number, p2: number): number[] {
    const d = p0 - 2 * p1 + p2
    if (Math.abs(d) < 1e-9) return []
    const t = (p0 - p1) / d
    return t > 0 && t < 1 ? [t] : []
}

function quadAt(p0: number, p1: number, p2: number, t: number): number {
    const u = 1 - t
    return u * u * p0 + 2 * u * t * p1 + t * t * p2
}

/**
 * Top-most y of a glyph's visible extent in normalized coords (y=0
 * baseline, +y up). Returns 0 for empty/missing input.
 */
export function glyphTopY(pathD: string | undefined): number {
    if (!pathD) return 0
    const tokens = pathD.match(/[A-Za-z]|-?\d+(?:\.\d+)?/g)
    if (!tokens) return 0

    let maxY = -Infinity
    let cx = 0
    let cy = 0
    let sx = 0
    let sy = 0
    let prev = ''
    let i = 0
    const num = () => parseFloat(tokens[i++])
    const push = (y: number) => {
        if (y > maxY) maxY = y
    }

    while (i < tokens.length) {
        let cmd = tokens[i]
        if (!/^[A-Za-z]$/.test(cmd)) {
            cmd =
                prev === 'M' ? 'L'
                : prev === 'm' ? 'l'
                : prev
        } else {
            i++
        }
        const U = cmd.toUpperCase()
        const rel = cmd !== U

        switch (U) {
            case 'Z':
                cx = sx
                cy = sy
                break
            case 'M': {
                const x = rel ? cx + num() : num()
                const y = rel ? cy + num() : num()
                push(y)
                cx = x
                cy = y
                sx = x
                sy = y
                break
            }
            case 'L':
            case 'T': {
                const x = rel ? cx + num() : num()
                const y = rel ? cy + num() : num()
                push(y)
                cx = x
                cy = y
                break
            }
            case 'H': {
                cx = rel ? cx + num() : num()
                push(cy)
                break
            }
            case 'V': {
                cy = rel ? cy + num() : num()
                push(cy)
                break
            }
            case 'C': {
                const x1 = rel ? cx + num() : num()
                const y1 = rel ? cy + num() : num()
                const x2 = rel ? cx + num() : num()
                const y2 = rel ? cy + num() : num()
                const x = rel ? cx + num() : num()
                const y = rel ? cy + num() : num()
                push(y)
                for (const t of cubExt(cy, y1, y2, y)) push(cubAt(cy, y1, y2, y, t))
                cx = x
                cy = y
                break
            }
            case 'S': {
                const x2 = rel ? cx + num() : num()
                const y2 = rel ? cy + num() : num()
                const x = rel ? cx + num() : num()
                const y = rel ? cy + num() : num()
                push(y)
                // No previous control to reflect — approximate as line; rare here.
                cx = x
                cy = y
                break
            }
            case 'Q': {
                const x1 = rel ? cx + num() : num()
                const y1 = rel ? cy + num() : num()
                const x = rel ? cx + num() : num()
                const y = rel ? cy + num() : num()
                push(y)
                for (const t of quadExt(cy, y1, y)) push(quadAt(cy, y1, y, t))
                cx = x
                cy = y
                break
            }
            case 'A': {
                // Skip rx, ry, rot, large, sweep, x, y
                i += 5
                const x = rel ? cx + num() : num()
                const y = rel ? cy + num() : num()
                push(y)
                cx = x
                cy = y
                break
            }
        }
        prev = cmd
    }
    return isFinite(maxY) ? maxY : 0
}
